import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import DropdownSelect from './common/DropdownSelect';
import { countryList } from '../const/const';
import type { DropdownSelectOption } from '../types/types';

interface EditSupplierCountryProps {
  show: boolean;
  onHide: () => void;
  supplierCountry: string; // ISO code
  onSave: (newCountry: string) => void;
  loading?: boolean;
  stackedOverModal?: boolean;
}

const EditSupplierCountry: React.FC<EditSupplierCountryProps> = ({
  show,
  onHide,
  supplierCountry,
  onSave,
  loading = false,
  stackedOverModal = false,
}) => {
  const [selectedCountry, setSelectedCountry] =
    useState<string>(supplierCountry);
  const [error, setError] = useState<boolean>(false);

  // Reset selection when modal opens or supplierCountry changes
  useEffect(() => {
    setSelectedCountry(supplierCountry);
    setError(false);
  }, [supplierCountry, show]);

  // Map countryList to DropdownSelect options
  const countryOptions: DropdownSelectOption[] = countryList.map((c) => ({
    id: c.isoCode,
    label: c.countryName,
  }));

  const handleCountrySelected = (
    option: DropdownSelectOption | DropdownSelectOption[],
  ) => {
    setSelectedCountry((option as DropdownSelectOption).id as string);
    setError(false);
  };

  const handleSave = () => {
    if (!selectedCountry) {
      setError(true);
      return;
    }
    onSave(selectedCountry);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className={stackedOverModal ? 'stacked-child-modal' : undefined}
      backdropClassName={
        stackedOverModal ? 'stacked-child-modal-backdrop' : undefined
      }
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Supplier Country</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="alert alert-warning">
          Warning, changing supplier country may affect the compliance status as
          countries have different risk levels associated with them.
        </div>
        <Form.Group>
          <Form.Label>Country</Form.Label>
          <DropdownSelect
            options={countryOptions}
            selectedIds={selectedCountry}
            onValueChanged={handleCountrySelected}
            isInvalid={error}
            placeholder="Select a country"
          />
          {error && (
            <Form.Text className="text-danger">
              Please select a country
            </Form.Text>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditSupplierCountry;
